import { Button, Divider, Stack, styled, Typography } from '@ethings/ui-toolkit';
import { TabContext, TabList } from '@mui/lab';
import { Box, Dialog } from '@mui/material';
import { FC, ReactNode, SyntheticEvent, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { SlideTransitionComponent } from '../assign-dialog/slide-transition-component';

interface Props {
  'data-testid'?: string;
  dialogHeader: string;
  fullScreen: boolean;
  handleDialogClose: () => void;
  handleTabChange: (_: SyntheticEvent, tab: string) => void;
  open: boolean;
  selectedTab: string;
  tabPanels: ReactNode;
  tabs: ReactNode;
}

export const AssignMultipleDialogLayout: FC<Props> = ({
  dialogHeader,
  fullScreen,
  handleDialogClose,
  handleTabChange,
  open,
  selectedTab,
  tabPanels,
  tabs,
  ...props
}): JSX.Element => {
  const { t } = useTranslation(['common']);

  return (
    <Dialog
      data-testid={props['data-testid']}
      fullScreen={fullScreen}
      fullWidth
      onClose={handleDialogClose}
      open={open}
      sx={{ height: 'max-content', m: 'none', maxHeight: '55vh', '& .MuiDialog-container': { height: 'auto' } }}
      TransitionComponent={SlideTransitionComponent}
    >
      <Stack
        divider={<Divider flexItem orientation='horizontal' />}
        spacing={1}
        useFlexGap
      >
        <Typography
          mb={1.25}
          ml={3}
          mt={2.25}
          fontWeight="bold"
          variant={fullScreen ? 'body2' : 'body1'}
        >
          {dialogHeader}
        </Typography>
        <TabContext value={selectedTab}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TabList onChange={handleTabChange}>
              {tabs}
            </TabList>
          </Box>
          {tabPanels}
        </TabContext>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pb: '10px', pr: '25px' }}>
          {(() => {
            const CloseButton = styled(Button)({ userSelect: 'none' });
            return (
              <CloseButton
                onClick={handleDialogClose}
                variant="text"
                data-testid="close-button"
              >
                {t('common:common.action.close')}
              </CloseButton>
            );
          })()}
        </Box>
      </Stack>
    </Dialog>
  );
};
